import { useEffect, useMemo, useRef } from "react";
import { motion, useAnimation } from "framer-motion";
import { cn } from "@/lib/utils";
import { SYMBOL_HEIGHT_PX, SlotSymbol } from "./types";
import { buildReelStrip, getStripStopOffset } from "./utils";

export interface ReelProps {
  targetSymbol: SlotSymbol;
  isSpinning: boolean;
  stopDelay: number;
  spinKey: number;
  isWinning?: boolean;
  onStopped?: () => void;
}

export function Reel({
  targetSymbol,
  isSpinning,
  stopDelay,
  spinKey,
  isWinning = false,
  onStopped,
}: ReelProps) {
  const controls = useAnimation();
  const onStoppedRef = useRef(onStopped);
  onStoppedRef.current = onStopped;

  const strip = useMemo(
    () => buildReelStrip(targetSymbol),
    [targetSymbol, spinKey]
  );

  const stopOffset = useMemo(() => getStripStopOffset(strip), [strip]);

  useEffect(() => {
    controls.set({ y: stopOffset });
  }, [stopOffset, controls]);

  useEffect(() => {
    if (!isSpinning) return;

    let cancelled = false;
    let stopTimer: ReturnType<typeof setTimeout>;

    const run = async () => {
      await controls.set({ y: 0 });

      controls.start({
        y: [0, -SYMBOL_HEIGHT_PX * 6],
        transition: {
          duration: 0.12,
          repeat: Infinity,
          ease: "linear",
        },
      });

      await new Promise<void>((resolve) => {
        stopTimer = setTimeout(resolve, stopDelay);
      });

      if (cancelled) return;

      controls.stop();

      await controls.start({
        y: stopOffset,
        transition: {
          duration: 0.85,
          ease: [0.22, 1, 0.36, 1],
          type: "spring",
          stiffness: 120,
          damping: 14,
          bounce: 0.45,
        },
      });

      if (!cancelled) {
        onStoppedRef.current?.();
      }
    };

    run();

    return () => {
      cancelled = true;
      clearTimeout(stopTimer);
      controls.stop();
    };
  }, [isSpinning, stopDelay, stopOffset, controls, spinKey]);

  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-xl",
        "h-24 w-[4.5rem] sm:h-28 sm:w-24",
        "border-2 border-amber-500/40 bg-gradient-to-b from-zinc-900 to-black",
        "shadow-[inset_0_4px_24px_rgba(0,0,0,0.8)]"
      )}
    >
      <div className="pointer-events-none absolute inset-x-0 top-0 z-10 h-6 bg-gradient-to-b from-black/80 to-transparent" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-6 bg-gradient-to-t from-black/80 to-transparent" />

      {isWinning && (
        <motion.div
          className="pointer-events-none absolute inset-0 z-20 rounded-xl ring-2 ring-amber-400"
          animate={{
            boxShadow: [
              "0 0 12px rgba(251,191,36,0.4)",
              "0 0 28px rgba(251,191,36,0.9)",
              "0 0 12px rgba(251,191,36,0.4)",
            ],
          }}
          transition={{ repeat: Infinity, duration: 0.9 }}
        />
      )}

      <motion.div animate={controls} className="flex flex-col items-center">
        {strip.map((symbol, index) => (
          <div
            key={`${spinKey}-${symbol}-${index}`}
            style={{ height: SYMBOL_HEIGHT_PX }}
            className="flex w-full shrink-0 items-center justify-center text-4xl select-none sm:text-5xl"
          >
            {symbol}
          </div>
        ))}
      </motion.div>
    </div>
  );
}
